import classNames from 'classnames'
import * as React from 'react'
import { Link } from 'react-router-dom'
import * as Api from 'src/api'
import Statements from 'src/assets/icons/customIcons/page-icons/Statements'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import NoContent from 'src/views/components/NoContent'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import { templateName } from './templateName'

export default function AdministratorStatementTemplatesPage(): JSX.Element {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('statement:statement_templates')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()
  const headers = useAuthenticatedHeaders()

  // All templates at once (~34), active and passive; they are added by developers, so there is no create or delete
  const { data: templates } = useApi({
    endpoint: Api.getAdministratorStatementTemplates,
    params: React.useMemo(() => ({ headers }), [headers]),
  })
  // The backend sends them unordered; old Argus listed them by id
  const sortedTemplates = React.useMemo(
    () => [...(templates ?? [])].sort((a, b) => Number(a.id) - Number(b.id)),
    [templates]
  )

  const listPath = `/${locale}/administrator/general/statement-templates`
  const breadcrumbsItems = [
    { page: `${t('system:general_information')} - ${t('statement:statement_templates')}`, path: listPath },
  ]

  return (
    <Section title={t('statement:statement_templates')} breadcrubms={breadcrumbsItems} icon={<Statements />}>
      {sortedTemplates.length === 0 ? (
        <NoContent header={t('error:records_not_found')} image={Warning} />
      ) : (
        <Table.Table className="mb-7">
          <Table.Thead>
            <Table.Tr>
              <Table.Th className="w-20">ID</Table.Th>
              <Table.Th>{t('common:name')}</Table.Th>
              <Table.Th className="w-40">{t('common:status')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedTemplates.map((template) => (
              <Table.Tr key={template.id} className={classNames(template.status === 0 && 'text-captionColor')}>
                <Table.Td>{template.id}</Table.Td>
                <Table.Td>
                  <Link to={`${listPath}/${template.id}`}>{templateName(template, locale)}</Link>
                </Table.Td>
                <Table.Td>{template.status === 1 ? t('common:active') : t('common:inactive')}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table.Table>
      )}
    </Section>
  )
}
